import { z } from "zod/v3";

export const ATSCategoryScoreSchema = z.object({
  category: z
    .enum([
      "Contact & Summary",
      "Experience Completeness",
      "Format & ATS Compatibility",
      "Skills & Education",
    ])
    .describe("Predefined ATS analysis category"),
  score: z.number().min(0).max(100).describe("Score for this category (0-100)"),
  feedback: z.string().describe("Brief feedback summarizing this category's performance"),
});

export const SectionIssueSchema = z.object({
  field: z.string().describe("Field/name within the section (e.g., Job Title, Bullet Points)"),
  issue: z.string().describe("Short description of the problem detected"),
  fix: z.string().describe("Concise instruction for how to fix the issue"),
  example: z.string().describe("Before/after or suggested replacement example for the fix"),
});

export const SectionFindingSchema = z.object({
  kind: z
    .enum(["strength", "issue"])
    .describe("Whether this is a strength or an issue in this section"),
  title: z.string().describe("Short title/headline for the finding"),
  description: z.string().describe("Detailed description of the finding"),
  severity: z
    .enum(["low", "medium", "high", "critical"])
    .describe("Severity level - use 'low' for strengths, 'medium'+ for issues"),
  field: z
    .string()
    .describe("Field within the section (e.g., 'Job Titles', 'Bullet Points', 'Skills List')"),
  fix: z.string().describe("Specific instruction on how to address this finding"),
  exampleBefore: z
    .string()
    .optional()
    .nullable()
    .describe("Optional example of current problematic text"),
  exampleAfter: z
    .string()
    .optional()
    .nullable()
    .describe("Optional example of improved text showing the fix"),
  impact: z
    .enum(["low", "medium", "high"])
    .describe("Expected impact on ATS score if addressed")
    .optional()
    .nullable(),
});

export const SectionAnalysisSchema = z.object({
  sectionName: z
    .string()
    .describe("Section analyzed: Experience, Skills, Education, Summary, Keywords, Format, etc."),
  score: z.number().min(0).max(100).describe("Section-specific score (0-100)"),
  status: z
    .enum(["excellent", "good", "needs_improvement", "missing"])
    .describe("Overall status for the section: excellent | good | needs_improvement | missing"),
  findings: z
    .array(SectionFindingSchema)
    .describe("List of specific findings (both strengths and issues) with fixes"),
  suggestions: z
    .array(z.string().describe("Actionable suggestion for improving this section"))
    .describe(
      "General suggestions for improving this section (can be empty for excellent sections)",
    )
    .nullable()
    .optional(),
});

export const EnhancedRecommendationSchema = z.object({
  type: z
    .enum(["critical", "important", "suggestion"])
    .describe("Priority/type of recommendation: critical | important | suggestion"),
  title: z.string().describe("Actionable title for the recommendation"),
  description: z.string().describe("Why this matters and what to change"),
  impact: z.enum(["high", "medium", "low"]).describe("Estimated impact level of this change"),
  section: z
    .string()
    .optional()
    .nullable()
    .describe("Which resume section this applies to (if applicable)"),
  currentExample: z.string().optional().nullable().describe("Current text/example (if available)"),
  improvedExample: z.string().describe("Concrete improved example to use"),
  actionableSteps: z
    .array(z.string().describe("Step in the recommended change"))
    .describe("Ordered steps to implement the recommendation"),
  estimatedEffortMin: z
    .number()
    .optional()
    .nullable()
    .describe("Estimated effort in minutes to implement (optional)"),
  confidence: z
    .number()
    .min(0)
    .max(100)
    .optional()
    .nullable()
    .describe("Confidence (0-100) in this recommendation and its impact"),
});

export const KeywordFindingSchema = z.object({
  kind: z
    .enum(["strength", "issue"])
    .describe("Whether this is a strength or an issue with keyword usage"),
  title: z.string().describe("Short title/headline for the keyword finding"),
  description: z.string().describe("Detailed description of the keyword finding"),
  severity: z
    .enum(["low", "medium", "high", "critical"])
    .describe("Severity level - use 'low' for strengths, 'medium'+ for issues"),
  field: z
    .string()
    .describe(
      "Field related to keywords (e.g., 'Technical Keywords', 'Industry Terms', 'Core Skills')",
    ),
  fix: z.string().describe("Specific instruction on how to address this keyword finding"),
  exampleBefore: z
    .string()
    .optional()
    .nullable()
    .describe("Optional example of current problematic keyword usage"),
  exampleAfter: z
    .string()
    .optional()
    .nullable()
    .describe("Optional example of improved keyword usage"),
  impact: z.enum(["low", "medium", "high"]).describe("Expected impact on ATS score if addressed"),
});

export const KeywordAnalysisSchema = z.object({
  score: z.number().min(0).max(100).describe("Overall keyword coverage score (0-100)"),
  status: z
    .enum(["excellent", "good", "needs_improvement", "missing"])
    .describe("Overall keyword optimization status"),
  findings: z
    .array(KeywordFindingSchema)
    .describe("List of specific keyword findings (both strengths and issues) with fixes"),
  suggestions: z
    .array(z.string().describe("Actionable suggestion for improving keyword usage"))
    .describe("General suggestions for improving keyword optimization")
    .optional()
    .nullable(),
  found: z
    .array(
      z.object({
        keyword: z.string().describe("Detected keyword/phrase"),
        locations: z
          .array(
            z
              .string()
              .describe(
                "Where the keyword appears, e.g., 'Experience - Job Title', 'Skills Section'",
              ),
          )
          .describe("Sections where the keyword was found"),
        frequency: z.number().describe("How many times it appears"),
      }),
    )
    .optional()
    .nullable()
    .describe("Keywords present in the resume"),
  missing: z
    .array(
      z.object({
        keyword: z.string().describe("Important missing keyword/phrase"),
        suggestedPlacement: z
          .array(z.string().describe("Where to add the keyword (section/field)"))
          .describe("Suggested locations to include the missing keyword"),
        example: z.string().describe("Example sentence showing how to incorporate the keyword"),
        priority: z
          .enum(["high", "medium", "low"])
          .describe("Priority of adding the missing keyword"),
      }),
    )
    .optional()
    .nullable()
    .describe("Important keywords to add"),
});

export const ATSAnalysisResultSchema = z.object({
  overallScore: z.number().min(0).max(100).describe("Overall ATS compatibility score (0-100)"),
  overallGrade: z
    .enum(["A+", "A", "A-", "B+", "B", "B-", "C+", "C", "C-", "D", "F"])
    .describe("Overall grade mapped from the score"),
  parseabilityScore: z
    .number()
    .min(0)
    .max(100)
    .describe("Parsing/format compatibility score (0-100)"),
  contentScore: z.number().min(0).max(100).describe("Content quality score (0-100)"),

  categories: z
    .array(ATSCategoryScoreSchema)
    .optional()
    .nullable()
    .describe("List of category scores and feedback"),

  strengths: z
    .array(z.string().describe("Strength statement"))
    .optional()
    .nullable()
    .describe("Top strengths detected"),
  weaknesses: z
    .array(z.string().describe("Weakness statement"))
    .optional()
    .nullable()
    .describe("Top weaknesses or areas to improve"),

  recommendations: z
    .array(EnhancedRecommendationSchema)
    .optional()
    .nullable()
    .describe("Prioritized recommendations with examples and steps"),

  keywords: KeywordAnalysisSchema.optional()
    .nullable()
    .describe("Keyword presence and gaps analysis"),

  sectionAnalysis: z
    .array(SectionAnalysisSchema)
    .optional()
    .nullable()
    .describe("Per-section scoring and key issues/suggestions"),

  industryInsights: z
    .object({
      detectedIndustry: z.string().describe("Best-guess industry inferred from resume"),
      confidence: z
        .number()
        .min(0)
        .max(100)
        .describe("Confidence (0-100) in the detected industry"),
      keyAdvice: z
        .array(z.string().describe("Advice point"))
        .describe("Short industry-specific tips"),
    })
    .optional()
    .nullable()
    .describe("Optional industry detection and guidance"),

  // Per-section findings and improvementExamples are nested under sectionAnalysis

  formatCompatibility: z
    .object({
      fileType: z.string().describe("Uploaded file type (e.g., pdf, docx, txt)"),
      isATSFriendly: z.boolean().describe("Whether format is ATS-friendly"),
      issues: z
        .array(z.string().describe("Format issue"))
        .describe("List of format issues that may affect parsing"),
      score: z.number().min(0).max(100).describe("Format/parseability sub-score (0-100)"),
    })
    .optional()
    .nullable()
    .describe("Format/parseability details and sub-score"),

  aiInsights: z
    .array(
      z.object({
        category: z
          .enum(["content_quality", "keyword_optimization", "writing_style"])
          .describe("Insight category"),
        title: z.string().describe("Short title for the insight"),
        description: z.string().describe("Detailed description of the insight"),
        section: z.string().optional().nullable().describe("Which resume section this applies to"),
        currentExample: z
          .string()
          .optional()
          .nullable()
          .describe("Example from the resume illustrating the insight"),
        actionableSteps: z
          .array(z.string())
          .optional()
          .nullable()
          .describe("Steps the user can take to address this insight"),
      }),
    )
    .optional()
    .nullable()
    .describe("AI-powered content insights (advisory, not scored)"),

  metadata: z
    .object({
      analyzedAt: z.string().describe("ISO timestamp when analysis completed"),
      processingTime: z.number().describe("Processing time in milliseconds"),
      wordCount: z.number().describe("Approximate word count of input text"),
      pageCount: z.number().describe("Approximate page count of input document"),
    })
    .describe("Server-side metadata for the analysis run"),
});

export type ATSAnalysisResult = z.infer<typeof ATSAnalysisResultSchema>;
export type AIInsight = NonNullable<ATSAnalysisResult["aiInsights"]>[number];
export type ATSCategoryScore = z.infer<typeof ATSCategoryScoreSchema>;
export type SectionIssue = z.infer<typeof SectionIssueSchema>;
export type SectionFinding = z.infer<typeof SectionFindingSchema>;
export type SectionAnalysis = z.infer<typeof SectionAnalysisSchema>;
export type EnhancedRecommendation = z.infer<typeof EnhancedRecommendationSchema>;
export type KeywordFinding = z.infer<typeof KeywordFindingSchema>;
export type KeywordAnalysis = z.infer<typeof KeywordAnalysisSchema>;

// Classification schema (internal intermediate format for deterministic ATS pipeline)
export {
  type ATSClassification,
  ATSClassificationSchema,
  type ATSRewrite,
  ATSRewriteSchema,
  type ContactInfoClassification,
  ContactInfoClassificationSchema,
  type DateConsistencyClassification,
  DateConsistencyClassificationSchema,
  type EducationEntryClassification,
  EducationEntryClassificationSchema,
  type ExperienceEntryClassification,
  ExperienceEntryClassificationSchema,
  type FormatClassification,
  FormatClassificationSchema,
  GroqATSClassificationSchema,
  type KeywordsClassification,
  KeywordsClassificationSchema,
  type OverallSignalsClassification,
  OverallSignalsClassificationSchema,
  type ResumeSnippets,
  ResumeSnippetsSchema,
  type SectionDetected,
  type SkillsClassification,
  SkillsClassificationSchema,
  type SummaryClassification,
  SummaryClassificationSchema,
} from "./classification";
